import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink , HttpLink} from '@apollo/client/core'
import { CONFIG_GRAPHQL_URL } from '@/config'
import { RemoveTypenameFromVariablesLink   } from "@apollo/client/link/remove-typename";
import { useLogger } from "@/composables/useLogger"
import { useLoaderStore } from "@/store/loader"
import { normalizeHttpError } from '../httpError'

const {sendLog} = useLogger()

const removeTypenameLink  = new RemoveTypenameFromVariablesLink ();

const httpLink = new HttpLink({
  uri: CONFIG_GRAPHQL_URL,
  credentials: 'include',
})

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers
    },
  }))
  return forward(operation)
})

const link = ApolloLink.from([
  authLink,
  removeTypenameLink,
  httpLink
]);


export const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
})

export const clearApolloCache = async () => {
  await apolloClient.clearStore();
}

export const mutate = async (mutation, variables) => {
  const loader = useLoaderStore()
  try {
    const result = await apolloClient.mutate({
      mutation,
      variables
    })
    loader.setAlert({
      message: "operation successful",
      color:"green",
      type: 'success',
      timeout: 5000
    })
    return result
  } catch (error) {
    const normalized = normalizeHttpError(error)
    sendLog("error", "GraphQL Mutation error", {metadata: {mutation, variables}, error})
    sendLog("normalized error", "GraphQL Mutation error normalized", normalized)
    loader.setAlert({
      message: "Operation can not be completed",
      type: 'error',
      timeout: 5000
    })
    throw error
  }
}

// Export query method for consistency
export const query = async (query, variables) => {
  
  try {
    const result = await apolloClient.query({
      query,
      variables
    })
    return result
  } catch (error) {
    const loader = useLoaderStore()
    const normalized = normalizeHttpError(error)
    sendLog("error", "GraphQL query error", {metadata: {variables, query}, error})
    sendLog("normalized error", "GraphQL query error normalized", normalized)
    loader.setAlert({
      message: normalized.message,
      type: 'error',
      timeout: 5000
    })
    throw error
  }
}
const OMIT_FIELDS = new Set([
  "__typename",
  "_id",
  "subsystems",
  "microsoft",
  "subsystemAccess",
  "groups",
  "accessLevel",
  "onyx",
  "clarity",
  "onyx_archive",
  "universalSystemConfig",
  "access_overrides",
  "lastAccess",
])

const FIELD_ALIASES = {
  company_hidden: "company",
  organisation_hidden: "organisation",
}

const isEmpty = (v) => v === ""

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v)

export const entityToMutationInput = (
  entity,
  isCreateMode = false,
  entitytype = null
) => {
  if (!entity) return entity

  const res = {}

  for (const [rawKey, rawValue] of Object.entries(entity)) {
    const key = FIELD_ALIASES[rawKey] ?? rawKey
    const value = rawValue

    if (isEmpty(value)) continue
    if (OMIT_FIELDS.has(key)) continue

    // CREATE MODE RULES
    if (isCreateMode) {
      if (rawKey === "pathPrefix") {
        if (entitytype !== "users") {
          res.path = `${entity.pathPrefix}/${entity.name}`
        }
        continue
      }
    }
    if(key === "cost"){
      res.cost = {
        currency: value.currency,
        costs: value.costs.map(item => ({
        ...item,
        costId:
          typeof item.costId === 'object'
            ? item.costId._id
            : item.costId
      }))}
      continue
     }
    // roles cleanup - remove __typename from each role object
    if (key === "roles" && Array.isArray(value)) {
      res.roles = value.map(({ __typename, ...rest }) => rest)
      continue
    }

    // relation flattening - for graphql mutations
    if (isPlainObject(value)) {
      if ("_id" in value) {
        res[key] = value._id
      } else {
        res[key] = entityToMutationInput(value, isCreateMode, entitytype)
      }
      continue
    }

    res[key] = value
  }

  return res
}

export const sanitizeGraphqlObject = (obj) => {
  if (!obj || typeof obj !== "object") return obj

  const res = Array.isArray(obj) ? [] : {}

  for (const [k, v] of Object.entries(obj)) {
    if (k === "__typename") continue
    if (k === "password") continue
    // if (k === "roles")res[k] = v
    if (v && typeof v === "object") {
      res[k] = sanitizeGraphqlObject(v)
    } else {
      res[k] = v
    }
  }

  return res
}